import React from "react";
import {
  Button,
  Calendar,
  Checkbox,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import MultiSelectField from "@/pages/chat/components/filter/MultiSelectField";
import {
  DocumentMetadataType,
  MetadataEnum,
  TenantDocumentMetadata,
  WorkspaceDocumentMetadata,
} from "@/models/metadata/metadata";
import { AlertTriangle, CalendarIcon, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { SelectChevronIcon } from "@/lib/svg-icons";
import { parseDate } from "./helpers";

interface FieldProps<T> {
  value: T;
  disabled: boolean;
  onChange: (value: T) => void;
}

// ===============================
// Parsing / Validation Utilities
// ===============================

// Core function to check if a value can be parsed to the expected field type
const checkValueParsing = (
  field: TenantDocumentMetadata | WorkspaceDocumentMetadata,
  value: string | number | boolean | Date | Date[] | string[] | undefined,
  messagePrefix: string = "Værdien"
): { hasIssue: boolean; rawValue: string; message: string } => {
  if (!value) return { hasIssue: false, rawValue: "", message: "" };

  const fieldType = isTenantDocumentMetadata(field)
    ? field.metadataTypeEnum
    : getWorkspaceMetadataType(field.metadata);

  let canParse = true;
  let message = "";

  switch (fieldType) {
    case MetadataEnum.NumberValue:
      if (typeof value === "string" && isNaN(Number(value))) {
        canParse = false;
        message = `${messagePrefix} "${value}" kan ikke konverteres til et tal.`;
      }
      break;
    case MetadataEnum.BooleanValue:
      if (typeof value === "string") {
        const lowerValue = value.toLowerCase();
        if (
          !["true", "false", "ja", "nej", "yes", "no", "1", "0"].includes(
            lowerValue
          )
        ) {
          canParse = false;
          message = `${messagePrefix} "${value}" kan ikke konverteres til en ja/nej værdi.`;
        }
      }
      break;
    case MetadataEnum.DateTimeValues:
      if (typeof value === "string" || typeof value === "number") {
        const date = parseDate(value?.toString());
        if (date === undefined) {
          canParse = false;
          message = `${messagePrefix} "${value}" kan ikke konverteres til en dato.`;
        }
      }
      break;
    case MetadataEnum.SelectValues:
      // For tenant fields, check if the value(s) are in the valid options
      if (isTenantDocumentMetadata(field)) {
        const validOptions = field.selectValues || [];
        let values: string[] = [];

        if (typeof value === "string") {
          // Split comma-separated string and check each value
          values = value
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
        } else if (Array.isArray(value)) {
          values = value.filter((v): v is string => typeof v === "string");
        }

        const invalidValues = values.filter((v) => !validOptions.includes(v));

        if (invalidValues.length > 0) {
          canParse = false;
          message = `${messagePrefix} "${invalidValues.join(
            ", "
          )}" er ikke en af de gyldige valgmuligheder.`;
        }
      }
      break;
    case MetadataEnum.StringValue:
      break;
  }

  return { hasIssue: !canParse, rawValue: String(value), message };
};

const hasParsingIssue = (
  field: TenantDocumentMetadata | WorkspaceDocumentMetadata,
  docxExtractionResults: { propertyName: string; value: string }[]
): { hasIssue: boolean; rawValue: string; message: string } => {
  if (!field.externalPropertyName || !docxExtractionResults?.length) {
    return { hasIssue: false, rawValue: "", message: "" };
  }
  const extractedValue = docxExtractionResults.find(
    (result) => result.propertyName === field.externalPropertyName
  )?.value;
  if (!extractedValue) return { hasIssue: false, rawValue: "", message: "" };
  return checkValueParsing(field, extractedValue, "Kunne ikke konvertere");
};

function checkCurrentValueParsingIssue(
  field: TenantDocumentMetadata | WorkspaceDocumentMetadata,
  value: string | number | boolean | Date | Date[] | string[] | undefined
) {
  return checkValueParsing(field, value, "Værdien");
}

// ===============================
// Metadata Type Guards
// ===============================
const isTenantDocumentMetadata = (
  field: TenantDocumentMetadata | WorkspaceDocumentMetadata
): field is TenantDocumentMetadata => {
  return "metadataTypeEnum" in field;
};

const isWorkspaceDocumentMetadata = (
  field: TenantDocumentMetadata | WorkspaceDocumentMetadata
): field is WorkspaceDocumentMetadata => {
  return "metadata" in field;
};

const isWorkspaceOnlyField = (
  fieldName: string,
  tenantFields: TenantDocumentMetadata[]
): boolean => {
  return !tenantFields.some((t) => t.name === fieldName);
};

const hasTypeMismatch = (
  fieldName: string,
  tenantFields: TenantDocumentMetadata[],
  workspaceField: WorkspaceDocumentMetadata
): boolean => {
  const tenantField = tenantFields.find((t) => t.name === fieldName);
  if (!tenantField) return false;
  const tenantType = tenantField.metadataTypeEnum;
  const workspaceType = getWorkspaceMetadataType(workspaceField.metadata);
  return tenantType !== workspaceType;
};

const hasInvalidSelectValues = (
  fieldName: string,
  tenantFields: TenantDocumentMetadata[],
  currentValue: string[] | undefined
): boolean => {
  const tenantField = tenantFields.find((t) => t.name === fieldName);
  if (
    !tenantField ||
    tenantField.metadataTypeEnum !== MetadataEnum.SelectValues
  )
    return false;
  if (!currentValue || currentValue.length === 0) return false;

  const validOptions = tenantField.selectValues || [];
  return currentValue.some((value) => !validOptions.includes(value));
};

const checkTenantFieldTypeMismatch = (
  field: TenantDocumentMetadata,
  value: string | number | boolean | Date | Date[] | string[] | undefined
): boolean => {
  // No value (undefined/null) or empty string is valid for clearing; only check when value is present
  if (value === undefined || value === null) return false;
  if (field.metadataTypeEnum === MetadataEnum.StringValue && value === "")
    return false;

  // Check if the value type matches the tenant field type
  switch (field.metadataTypeEnum) {
    case MetadataEnum.StringValue:
      return typeof value !== "string";
    case MetadataEnum.NumberValue:
      return typeof value !== "number";
    case MetadataEnum.BooleanValue:
      return typeof value !== "boolean";
    case MetadataEnum.DateTimeValues:
      return !Array.isArray(value);
    case MetadataEnum.SelectValues:
      return !Array.isArray(value);
    default:
      return false;
  }
};

const getWorkspaceMetadataType = (
  metadata: DocumentMetadataType
): MetadataEnum | null => {
  if (metadata.stringValue != null) return MetadataEnum.StringValue;
  if (metadata.numberValue != null) return MetadataEnum.NumberValue;
  if (metadata.booleanValue != null) return MetadataEnum.BooleanValue;
  if (metadata.dateTimeValues != null) return MetadataEnum.DateTimeValues;
  if (metadata.selectValues != null) return MetadataEnum.SelectValues;
  return null;
};

// ===============================
// Small UI Helpers
// ===============================
const WarningIcon = ({
  warningMessage,
}: {
  warningMessage?: {
    message: string | undefined;
    type: "error" | "warning" | "info" | "success" | "none";
  };
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div
        className={cn(
          "flex items-center justify-center w-5 h-5",
          !warningMessage && "pointer-events-none",
          warningMessage?.type === "error"
            ? "text-destructive"
            : warningMessage?.type === "warning"
              ? "text-warning"
              : warningMessage?.type === "info"
                ? "text-primary"
                : warningMessage?.type === "success"
                  ? "text-success"
                  : "text-muted-foreground"
        )}
      >
        <AlertTriangle
          className={`w-4 h-4 ${
            !warningMessage && "hidden pointer-events-none"
          }`}
        />
      </div>
    </TooltipTrigger>
    <TooltipContent>
      <p className="text-sm">{warningMessage?.message}</p>
    </TooltipContent>
  </Tooltip>
);

const DeleteButton = ({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    className="text-destructive size-9 hover:text-destructive"
    disabled={disabled}
  >
    <Trash2 className="h-4 w-4" />
  </Button>
);

// ===============================
// Field Renderers
// ===============================
const StringField = ({
  value,
  disabled,
  onChange,
  suggestedValues = [],
}: FieldProps<string | undefined> & {
  suggestedValues?: string[];
  fieldName?: string;
}) => {
  const [open, setOpen] = React.useState(false);
  const hasSuggestions = suggestedValues.length > 0;
  const displayValue = value ?? "";
  const filteredSuggestions = React.useMemo(() => {
    if (!displayValue.trim()) return suggestedValues;
    const q = displayValue.toLowerCase().trim();
    return suggestedValues.filter((v) => v.toLowerCase().includes(q));
  }, [suggestedValues, displayValue]);
  const placeholder = hasSuggestions
    ? "Indtast eller vælg..."
    : "Indtast tekst...";

  // Same trigger styling as MultiSelectField ("Vælg tags...") for consistency
  const triggerClassName = cn(
    "flex min-h-9 h-auto w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
    "focus:ring-1 focus:ring-primary focus-visible:ring-1 focus-visible:ring-primary hover:bg-accent/50"
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={triggerClassName}
          disabled={disabled}
          type="button"
        >
          {displayValue ? (
            <span className="truncate text-left font-normal">
              {displayValue}
            </span>
          ) : (
            <span className="font-normal pointer-events-none text-muted-foreground">
              {placeholder}
            </span>
          )}
          <SelectChevronIcon className="shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <div className="p-2 border-b border-border">
          <Input
            type="text"
            placeholder={placeholder}
            value={displayValue}
            disabled={disabled}
            onChange={(e) => !disabled && onChange(e.target.value || undefined)}
            className="h-9"
            autoFocus
          />
        </div>
        {hasSuggestions && (
          <Command className="h-auto" shouldFilter={false}>
            <CommandList className="max-h-[200px] overflow-y-auto">
              <CommandGroup heading="Eksisterende værdier">
                {filteredSuggestions.length > 0 ? (
                  filteredSuggestions.map((option) => (
                    <CommandItem
                      key={option}
                      value={option}
                      onSelect={() => {
                        if (!disabled) {
                          onChange(option);
                          setOpen(false);
                        }
                      }}
                    >
                      {option}
                    </CommandItem>
                  ))
                ) : (
                  <div className="py-2 px-2 text-sm text-muted-foreground">
                    Ingen matchende værdier
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
};

const NumberField = ({
  value,
  disabled,
  onChange,
}: FieldProps<number | undefined>) => {
  return (
    <Input
      type="number"
      value={typeof value === "number" ? value : value || ""}
      disabled={disabled}
      onChange={(e) =>
        !disabled &&
        onChange(e.target.value ? Number(e.target.value) : undefined)
      }
      placeholder="Indtast tal..."
    />
  );
};

const BooleanField = ({
  value,
  disabled,
  onChange,
}: FieldProps<boolean | string | undefined>) => {
  const parsedValue =
    typeof value === "string"
      ? value.toLowerCase() === "true" ||
        value.toLowerCase() === "ja" ||
        value.toLowerCase() === "yes" ||
        value.toLowerCase() === "1"
        ? true
        : value.toLowerCase() === "false" ||
            value.toLowerCase() === "nej" ||
            value.toLowerCase() === "no" ||
            value.toLowerCase() === "0"
          ? false
          : undefined
      : value;
  return (
    <div className="flex justify-end gap-4">
      <label className="flex items-center gap-2">
        <Checkbox
          checked={parsedValue === true}
          disabled={disabled}
          onCheckedChange={(c) => onChange(c ? true : undefined)}
        />{" "}
        Ja
      </label>
      <label className="flex items-center gap-2">
        <Checkbox
          checked={parsedValue === false}
          disabled={disabled}
          onCheckedChange={(c) => onChange(c ? false : undefined)}
        />{" "}
        Nej
      </label>
    </div>
  );
};

const DateField = ({
  value,
  disabled,
  onChange,
}: FieldProps<Date | string | null | undefined>) => {
  const [open, setOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState("");

  const toDisplayString = (v: Date | string | null | undefined): string => {
    if (!v) return "";
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d.getTime())) return "";
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  const resolvedDate = React.useMemo(() => {
    if (!value) return undefined;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  React.useEffect(() => {
    setInputText(toDisplayString(value));
  }, [value]);

  const handleBlur = () => {
    const parsed = parseDate(inputText);
    if (parsed?.[0]) {
      onChange(parsed[0]);
      setInputText(toDisplayString(parsed[0]));
    } else if (!inputText.trim()) {
      onChange(null);
    } else {
      setInputText(toDisplayString(value));
    }
  };

  return (
    <div className="flex gap-1 w-full">
      <Input
        value={inputText}
        disabled={disabled}
        placeholder="dd/mm/yyyy"
        onChange={(e) => setInputText(e.target.value)}
        onBlur={handleBlur}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={resolvedDate}
            onSelect={(d) => {
              onChange(d ?? null);
              setOpen(false);
            }}
            captionLayout="dropdown"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const SelectField = ({
  value,
  disabled,
  onChange,
  options,
}: FieldProps<string[] | string | undefined> & { options: string[] }) => {
  let processedValue: string[] = [];
  if (value && typeof value === "string") {
    // Split comma-separated string into array and trim whitespace
    processedValue = (value as string)
      .split(",")
      .map((v: string) => v.trim())
      .filter((v: string) => v.length > 0);
  } else if (Array.isArray(value)) {
    processedValue = value;
  }

  // Filter out invalid values for DISPLAY ONLY (keep them in the actual value for warnings)
  const validValues = processedValue.filter((v) => options.includes(v));

  // Convert string to array, keeping ALL values (including invalid ones)
  // This ensures warnings can detect invalid values
  React.useEffect(() => {
    if (value && typeof value === "string") {
      const processed = value
        .split(",")
        .map((v: string) => v.trim())
        .filter((v: string) => v.length > 0);
      if (processed.length > 0) {
        onChange(processed);
      }
    }
  }, [value, onChange]);

  return (
    <MultiSelectField
      options={options}
      value={validValues}
      disabled={disabled}
      placeholder="Vælg tags..."
      className="w-full -mt-2"
      onChange={(selected: string) => {
        if (disabled) return;
        const newValues = validValues.includes(selected)
          ? validValues.filter((v: string) => v !== selected)
          : [...validValues, selected];
        onChange(newValues);
      }}
      onlyShowSelectedNumber={true}
    />
  );
};

// ===============================
// Main Component
// ===============================
export interface MetadataDialogContentProps {
  field: TenantDocumentMetadata | WorkspaceDocumentMetadata;
  value: string | number | boolean | Date | Date[] | string[] | undefined;
  tenantFields?: TenantDocumentMetadata[];
  isUploadMode?: boolean;
  /** Currently used values for this field (for free-text suggestions dropdown) */
  suggestedValues?: string[];
  onChange: (
    value: string | number | boolean | Date | Date[] | string[] | undefined
  ) => void;
  onDelete: () => void;
  docxExtractionResults?: { propertyName: string; value: string }[];
}

const MetadataDialogContent: React.FC<MetadataDialogContentProps> = ({
  field,
  value,
  tenantFields = [],
  isUploadMode = false,
  suggestedValues = [],
  onChange,
  onDelete,
  docxExtractionResults = [],
}) => {
  // Parsing and validation
  const parsingIssue = hasParsingIssue(field, docxExtractionResults);
  const currentValueParsingIssue = checkCurrentValueParsingIssue(field, value);
  const finalParsingIssue = parsingIssue.hasIssue
    ? parsingIssue
    : currentValueParsingIssue;

  let warningMessage:
    | {
        message: string | undefined;
        type: "error" | "warning" | "info" | "success" | "none";
      }
    | undefined;
  let disabled = false;

  // Validation logic for different field types (only in edit mode)
  if (!isUploadMode) {
    if (isWorkspaceDocumentMetadata(field)) {
      const isWorkspaceOnly = isWorkspaceOnlyField(field.name, tenantFields);
      if (isWorkspaceOnly) {
        warningMessage = {
          message:
            "Denne metadata findes ikke i organisationen og vil blive fjernet permanent ved sletning.",
          type: "warning",
        };
      } else if (hasTypeMismatch(field.name, tenantFields, field)) {
        disabled = true;
        warningMessage = {
          message:
            "Typen er blevet ændret. Du skal slette den gamle værdi for at kunne indtaste en ny.",
          type: "error",
        };
      }
    } else if (isTenantDocumentMetadata(field)) {
      // Check for type mismatches when using tenant fields
      const hasTypeMismatch = checkTenantFieldTypeMismatch(field, value);
      if (hasTypeMismatch) {
        disabled = true;
        warningMessage = {
          message:
            "Værdien matcher ikke felttypen. Du skal slette den gamle værdi for at kunne indtaste en ny.",
          type: "error",
        };
      } else if (
        field.metadataTypeEnum === MetadataEnum.SelectValues &&
        hasInvalidSelectValues(
          field.name,
          tenantFields,
          value as string[] | undefined
        )
      ) {
        // Check for invalid select values (when tenant field has different options)
        disabled = true;
        // Use specific parsing error ONLY if from DOCX extraction, not current value
        const errorMessage = parsingIssue.hasIssue
          ? parsingIssue.message
          : "De valgte værdier er ikke længere gyldige. Du skal slette de gamle værdier for at kunne vælge nye.";
        warningMessage = {
          message: errorMessage,
          type: "error",
        };
      }
    }
  }

  // Handle warnings based on mode
  if (isUploadMode) {
    // In upload mode, only show parsing issues (blue info) - no value is set, user can enter manually
    if (finalParsingIssue.hasIssue) {
      warningMessage = { message: finalParsingIssue.message, type: "info" };
      value = undefined;
    }
  } else {
    // In edit mode, show type mismatches and validation errors (yellow/red warnings)
    // Only show parsing issues if no other warnings are present
    if (finalParsingIssue.hasIssue && !warningMessage) {
      warningMessage = { message: finalParsingIssue.message, type: "info" };
    }
  }

  // Render by type
  const renderField = () => {
    if (isTenantDocumentMetadata(field)) {
      switch (field.metadataTypeEnum) {
        case MetadataEnum.StringValue:
          return (
            <StringField
              value={value as string | undefined}
              disabled={disabled}
              onChange={onChange}
              suggestedValues={suggestedValues}
              fieldName={field.name}
            />
          );
        case MetadataEnum.NumberValue:
          return (
            <NumberField
              value={value as number | undefined}
              disabled={disabled}
              onChange={onChange}
            />
          );
        case MetadataEnum.BooleanValue:
          return (
            <BooleanField
              value={value as boolean | string | undefined}
              disabled={disabled}
              onChange={onChange}
            />
          );
        case MetadataEnum.DateTimeValues: {
          const dates = Array.isArray(value)
            ? value
            : (parseDate(value?.toString()) ?? [null]);
          return (
            <div className="flex gap-2 w-full">
              <DateField
                value={dates?.[0] ?? null}
                disabled={disabled}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(d: any) => onChange([d])}
              />
              {/* TODO: Add back in when we see a purpose for dateranges */}
              {/* <DateField
                value={dates[1] as Date | null | undefined}
                disabled={disabled || !dates[0]}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(d: any) => onChange([dates[0], d])}
              /> */}
            </div>
          );
        }
        case MetadataEnum.SelectValues:
          return (
            <SelectField
              value={value as string[] | undefined}
              disabled={disabled}
              onChange={onChange}
              options={field.selectValues || []}
            />
          );
      }
    }

    if (isWorkspaceDocumentMetadata(field)) {
      const m = field.metadata;
      if (m.stringValue != null)
        return (
          <StringField
            value={value as string | undefined}
            disabled={disabled}
            onChange={onChange}
            suggestedValues={suggestedValues}
            fieldName={field.name}
          />
        );
      if (m.numberValue != null)
        return (
          <NumberField
            value={value as number | undefined}
            disabled={disabled}
            onChange={onChange}
          />
        );
      if (m.booleanValue != null)
        return (
          <BooleanField
            value={value as boolean | undefined}
            disabled={disabled}
            onChange={onChange}
          />
        );
      if (m.dateTimeValues != null) {
        const dates = Array.isArray(value)
          ? value
          : (parseDate(value?.toString()) ?? [null]);
        return (
          <div className="flex gap-2 w-full">
            <DateField
              value={dates?.[0] ?? null}
              disabled={disabled}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(d: any) => onChange([d, dates?.[1]])}
            />
            {/* TODO: Add back in when we see a purpose for dateranges */}
            {/* <DateField
              value={dates[1] as Date | null | undefined}
              disabled={disabled || !dates[0]}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(d: any) => onChange([dates[0], d])}
            /> */}
          </div>
        );
      }
      if (m.selectValues != null)
        return (
          <SelectField
            value={value as string[] | undefined}
            disabled={disabled}
            onChange={onChange}
            options={m.selectValues}
          />
        );
    }

    return null;
  };

  return (
    <div className="flex w-full items-center gap-2">
      <WarningIcon warningMessage={warningMessage} />
      <div className="flex-1">{renderField()}</div>
      <DeleteButton disabled={!value && value !== false} onClick={onDelete} />
    </div>
  );
};

export default MetadataDialogContent;
